import { ProgressStore, type ClearReceipt, type DrawReceipt, type Progress, type Receipt } from '../game/storage/progressStore';
import type { CosmeticKind } from '../game/content/cosmetics';
import type { PullCount } from '../game/gacha/gachaRules';

export class ProgressController {
  private readonly listeners = new Set<(profile: Progress) => void>();
  private readonly channel: BroadcastChannel | null;
  public profile: Progress | null = null;
  constructor(public readonly store: ProgressStore) {
    this.channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(store.databaseName);
    if (this.channel) this.channel.onmessage = () => { void this.refresh().catch(() => {}); };
    document.addEventListener('visibilitychange', this.onVisibility);
  }
  onChange(listener: (profile: Progress) => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  private publish(profile: Progress): Progress {
    if (this.profile && profile.revision < this.profile.revision) return this.profile;
    this.profile = profile;
    for (const listener of this.listeners) listener(profile);
    return profile;
  }
  async refresh(): Promise<Progress> { return this.publish(await this.store.loadProgress()); }
  async draw(id: string, count: PullCount): Promise<DrawReceipt> {
    const saved = await this.store.draw(id, count);
    this.publish(saved.profile); this.channel?.postMessage(saved.profile.revision);
    return saved.result;
  }
  async awardClear(id: string): Promise<ClearReceipt> {
    const saved = await this.store.awardClear(id);
    this.publish(saved.profile); this.channel?.postMessage(saved.profile.revision);
    return saved.result;
  }
  async awardPhase(id: string, phase: number) {
    const saved = await this.store.awardPhase(id, phase);
    this.publish(saved.profile);
    if (saved.awardedAmount > 0) this.channel?.postMessage(saved.profile.revision);
    return saved;
  }
  async equip(kind: CosmeticKind, id: string): Promise<void> {
    const saved = await this.store.equip(kind, id);
    this.publish(saved); this.channel?.postMessage(saved.revision);
  }
  acknowledge(receipt: Receipt): Promise<void> { return this.store.acknowledgeReceipt(receipt); }
  private readonly onVisibility = (): void => { if (!document.hidden) void this.refresh().catch(() => {}); };
  destroy(): void { document.removeEventListener('visibilitychange', this.onVisibility); this.channel?.close(); this.listeners.clear(); void this.store.close(); }
}
